import React, { memo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Icon from '@mdi/react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { mdiCheck, mdiCheckAll } from '@mdi/js';
import DeleteIcon from '@material-ui/icons/Delete';
import { CircularProgress, Tooltip } from '@material-ui/core';
import { withTranslation } from 'react-i18next';
import ChatFileViewer from './ChatFileViewer';
import { MessageProps } from '../../../shared/prop-types/ChatProps';
import {
  getTimeZoneDate,
  isLink,
  separateLinkFromMessage,
} from '../../../shared/utils/smartutils';

const ChatBubble = (props) => {
  const {
    t,
    message,
    active,
    uploadFileToServer,
    currentMessages,
    deleteMessage,
  } = props;

  const bubbleClass = classNames({
    chat__bubble: true,
    'chat__bubble--active': active,
  });

  const uploadFileAgain = () => {
    uploadFileToServer(message.uploadFileError.file, message, currentMessages);
  };

  const showDeleteMessage = () => {
    return active && !message.read;
  };

  const [text, url, textAfter] = separateLinkFromMessage(message.message);

  const buildChatBubble = () => {
    if (message.file) {
      return (
        <div className="chat__bubble-message-wrap--file">
          <p className="chat__bubble-contact-name">{message.senderName}</p>
          <ChatFileViewer
            fileUrl={message.file}
            mimeType={message.fileType}
            fileSize={message.fileSize}
            uploadFileError={message.uploadFileError}
            uploadFileAgain={uploadFileAgain}
            fileName={message.fileName}
          />
          <div>
            <span style={{ whiteSpace: 'pre-line' }}>{message.message}</span>
          </div>
          <p className="chat__bubble-date">
            {format(
              getTimeZoneDate(message.sentAtUtcInMilli),
              'dd MMM, HH:mm:ss',
              {
                locale: ptBR,
              }
            )}
          </p>
        </div>
      );
    }
    return (
      <div className="chat__bubble-message-wrap--default">
        <p className="chat__bubble-contact-name">{message.senderName}</p>
        <p className="chat__bubble-contact-name">{message.read}</p>
        {isLink(message.message) ? (
          <div className="text-wrapper">
            <span>{text}</span>
            <a key="url" href={url} target="_blank" rel="noreferrer">
              {url}
            </a>
            <span> {textAfter}</span>
          </div>
        ) : (
          <span style={{ whiteSpace: 'pre-line' }}>{message.message}</span>
        )}
        <div className="chat__bubble-date-wrap">
          {message.read ? (
            <Icon path={mdiCheckAll} size={0.5} />
          ) : (
            <Icon path={mdiCheck} size={0.5} />
          )}
          <p className="chat__bubble-date">
            {format(
              getTimeZoneDate(message.sentAtUtcInMilli),
              'dd MMM, HH:mm',
              {
                locale: ptBR,
              }
            )}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className={bubbleClass}>
      <div className="chat__bubble-message-wrap">
        {buildChatBubble()}
        {showDeleteMessage() && (
          <>
            {message.isDeleting ? (
              <CircularProgress size={20} className="chat__ico-loading" />
            ) : (
              <Tooltip title={t('general.remove')}>
                <DeleteIcon
                  className="chat__ico-delete"
                  onClick={() => deleteMessage(message)}
                />
              </Tooltip>
            )}
          </>
        )}
      </div>
    </div>
  );
};

ChatBubble.propTypes = {
  t: PropTypes.func.isRequired,
  message: MessageProps,
  active: PropTypes.bool,
  uploadFileToServer: PropTypes.func.isRequired,
  deleteMessage: PropTypes.func.isRequired,
  currentMessages: PropTypes.arrayOf(PropTypes.shape(PropTypes.shape)),
};

ChatBubble.defaultProps = {
  active: false,
  message: {},
  currentMessages: [],
};

export default withTranslation('common')(memo(ChatBubble));
